
import { step, APP_STEP } from '../actions/app'
import {
  audioChatStartRequest,
  audioChatStartRequestCancel,
  audioChatStartResponse,
  audioChatStarted,
  audioChatEnd,
  audioChatMuteMicrophone,
  selectAudioInput,
  startListenMediaDevicesChange,
  stopListenMediaDevicesChange,
  restartChatListenMediaDevicesDone,
  audioChatStatsRequest,
  audioChatStatsResponse,
  audioChatStats,
} from '../actions/media'
import {
  tbShowModal,
  tbHideModal,
  tbCloseModal,
} from '../actions/toolbar'
import { TB_MODALS } from '../components/toolbar/Toolbar'
import { CommandsMessageCreator } from '../lib/create/CommandsMessageCreator'
import { audioChatTrackChange, sendCommand } from '../actions/webrtcConnection'
import { logger } from '../lib/utils/logger'

const processAudioStats = true // enable/disable audio stats processing

let listenAudioStatsTimer = null
const listenAudioStats = ({ store = null, enabled = true }) => {
  if (!processAudioStats || !enabled) {
    clearTimeout(listenAudioStatsTimer)
    return
  }
  store.dispatch(audioChatStatsRequest())
  listenAudioStatsTimer = setTimeout(() => {
    const { audio: { chatStarted } } = store.getState()
    if (chatStarted) {
      listenAudioStats({ store, enabled })
    }
  }, 1000)
}

const processStats = ({ outputStats, inputStats }) => {
  const stats = {
    audioLevel: 0,
    audioLevelPeer: 0,
  }

  try {
    for (const e of outputStats) {
      if (e[1].type === 'media-source') {
        stats.audioLevel = e[1].audioLevel
      }
    }
    for (const e of inputStats) {
      if (e[1].type === 'track') {
        stats.audioLevelPeer = e[1].audioLevel
      }
    }
  } catch (error) {
    stats.audioLevel = 0
    stats.audioLevelPeer = 0
  }

  return stats
}

/* eslint-disable complexity */
const chatActionNotValid = ({ action, store }) => {
  const {
    audio: {
      chatRequest,
      requestByPeer,
      chatStarted,
    },
    features: {
      audioChat: audioChatFeature,
    }
  } = store.getState()

  // check only AUDIO_CHAT... actions, the others should pass
  if (!action.type.startsWith('AUDIO_CHAT')) {
    return false
  }

  if (!audioChatFeature) {
    return 'audio chat not implemented'
  }

  if (action.type === audioChatStartRequest.toString()) {
    if (chatStarted) { return 'audio chat is started' }
    if (chatRequest && requestByPeer) { return 'a chat request from peer is waiting for response' }
    if (chatRequest && !requestByPeer && !action.payload.requestByPeer) {
      return 'a chat request from you is already waiting the response from peer'
    }
    // chatRequest && !requestByPeer && action.payload.requestByPeer - should pass:
    // you send a request and then, before a response (which resets the chatRequest) come,
    // arrives a request from peer => both sides send requests in the same time; the agent request
    // should be allowed and the client should change the 'waiting for response' message with 'accept/reject' message
    // (an agrrement: the peer request replace your request)
    return false
  }

  if (action.type === audioChatStartRequestCancel.toString()) {
    if (chatStarted) { return 'audio chat is started' }
    if (!chatRequest) { return 'no chat request is waiting for response' }
    if (requestByPeer && !action.payload.cancelByPeer) { return 'you cannot cancel a request from peer' }
    if (!requestByPeer && action.payload.cancelByPeer) { return 'peer cannot cancel your request' }
    return false
  }

  if (action.type === audioChatStartResponse.toString()) {
    if (chatStarted) { return 'audio chat is started' }
    if (!chatRequest) { return 'no chat request is waiting for response' }
    if (requestByPeer && action.payload.fromPeer) { return 'peer cannot answer its own request' }
    if (!requestByPeer && !action.payload.fromPeer) { return 'you cannot answer your own request' }
    return false
  }

  if (action.type === audioChatEnd.toString()) {
    if (!chatStarted) { return 'audio chat is not started' }
    return false
  }

  return false
}
/* eslint-enable complexity */

const commandsMessageCreator = new CommandsMessageCreator()

const webRtc_UpdateAudioChatTrack = ({ store, selectedAudioInput, silenceAudio }) => {
  const track = (selectedAudioInput && selectedAudioInput.track) ? selectedAudioInput.track : silenceAudio.track
  store.dispatch(audioChatTrackChange(track))
}

const sendMute = ({ store, muted }) => {
  let cmdBinary = null
  logger(` ==== send ${muted ? 'mute' : 'unmute'} command =====`)
  if (muted) {
    cmdBinary = commandsMessageCreator.createAudioChatMicrophoneMuted()
  } else {
    cmdBinary = commandsMessageCreator.createAudioChatMicrophoneUnmuted()
  }
  store.dispatch(sendCommand(cmdBinary))
}

export const audioChatMiddleware = (store) => (next) => (action) => {
  const actions = {
    [step]: (payload) => {
      if (payload === APP_STEP.RUNNING) {
        const { audio: { restartStatus: { started, byPeer, chatRequest, requestByPeer } } } = store.getState()
        if (started && !byPeer) {
          logger(`------step APP_STEP.RUNNING; restart chat started by client (${!byPeer})`)
          store.dispatch(startListenMediaDevicesChange({ doneAction: restartChatListenMediaDevicesDone() }))
        }
        if (!started && chatRequest && !requestByPeer) {
          logger('------step APP_STEP.RUNNING; chat requested by client was active')
          store.dispatch(startListenMediaDevicesChange({ doneAction: restartChatListenMediaDevicesDone() }))
        }
      }
    },
    [audioChatStartRequest]: (payload) => {
      const { requestByPeer } = payload
      const { audio: { restartStatus }, toolbar: { modal } } = store.getState()
      logger(`------audioChatStartRequest (requestByPeer: ${requestByPeer})`)

      if (requestByPeer) {
        if (restartStatus.started && restartStatus.byPeer) {
          // restart a chat started be peer; listen media device change
          store.dispatch(startListenMediaDevicesChange({ doneAction: restartChatListenMediaDevicesDone() }))
        }
        if (modal[TB_MODALS.AUDIO_CHAT_REQUEST]) {
          store.dispatch(tbCloseModal(TB_MODALS.AUDIO_CHAT_REQUEST))
        }
        if (modal[TB_MODALS.AUDIO_CHAT_REJECTED]) {
          store.dispatch(tbCloseModal(TB_MODALS.AUDIO_CHAT_REJECTED))
        }
      } else {
        // requested by you; send request to the agent
        const cmdBinary = commandsMessageCreator.createAudioChatStartRequest()
        store.dispatch(sendCommand(cmdBinary))
        if (
          (!restartStatus.chatRequest && !restartStatus.started) || // it's not a restart
          (restartStatus.chatRequest && !restartStatus.requestByPeer)
        ) {
          if (modal[TB_MODALS.AUDIO_CHAT_REQUEST] === 'hidden') {
            store.dispatch(tbHideModal(TB_MODALS.AUDIO_CHAT_REQUEST))
          } else {
            store.dispatch(tbShowModal(TB_MODALS.AUDIO_CHAT_REQUEST))
          }
        }

        // resume the silenceAudio audioContext as a result of a user interaction
        // (you requested the chat by clicking a button)

        // const { audio: { silenceAudio } } = store.getState()
        // silenceAudio.context.resume()
        //   .then(() => logger(' -- audio context resumed'))
        //   .catch((err) => logger(' -- error resume audio context', err))
      }
    },
    [restartChatListenMediaDevicesDone]: () => {
      // restarting a chat; media device change query finished
      const { audio: { restartStatus: { started, byPeer, chatRequest, requestByPeer } } } = store.getState()
      if (chatRequest && !requestByPeer) {
        store.dispatch(audioChatStartRequest({ requestByPeer: false }))
        return
      }
      if (!started) {
        return
      }
      if (byPeer) {
        // started be peer; send response to agent
        logger('------restartChatListenMediaDevicesDone; send response')
        store.dispatch(audioChatStartResponse({ fromPeer: false, response: true }))
      } else {
        // started by client; send request to the agent
        // (the agent should automatically send the response)
        store.dispatch(audioChatStartRequest({ requestByPeer: false }))
      }
    },
    [audioChatStartRequestCancel]: (payload) => {
      const { cancelByPeer } = payload
      logger(`------audioChatStartRequestCancel (cancelByPeer: ${cancelByPeer})`)
      if (!cancelByPeer) {
        // request cancelled by you; send cancel cammand to the agent
        const cmdBinary = commandsMessageCreator.createAudioChatStartRequestCancel()
        store.dispatch(sendCommand(cmdBinary))
        store.dispatch(tbCloseModal(TB_MODALS.AUDIO_CHAT_REQUEST))
      }
      store.dispatch(stopListenMediaDevicesChange())
    },
    [audioChatStartResponse]: ({ fromPeer, response }) => {
      const {
        audio: {
          chatRequest,
          selectedAudioInput,
          restartStatus: {
            started: rsStarted,
            microphoneMuted: rsMuted,
          }
        }
      } = store.getState()
      logger('------audioChatStartResponse')
      if (chatRequest && !fromPeer) { // answer from you
        // send response to agent
        const cmdBinary = commandsMessageCreator.createAudioChatStartResponse(response)
        store.dispatch(sendCommand(cmdBinary))
      }
      if (chatRequest && response) {
        store.dispatch(audioChatStarted()) // note: chat started will reset the restartStatus
        if (rsStarted && rsMuted) {
          // it's a restart and client micro was muted; dispatch the mute action
          // (updates client ui/state and send the mute command to the peer)
          store.dispatch(audioChatMuteMicrophone(rsMuted))
        } else {
          // chat normal start (mute is false) or restart with mute false
          // send the mute command to the peer (micro may be missing)
          sendMute({ store, muted: !(selectedAudioInput && selectedAudioInput.track) })
        }
      }
      // audio chat rejected; stop listening media devices change
      if (!response) {
        store.dispatch(stopListenMediaDevicesChange())
        if (fromPeer) {
          store.dispatch(tbCloseModal(TB_MODALS.AUDIO_CHAT_REQUEST))
          store.dispatch(tbShowModal(TB_MODALS.AUDIO_CHAT_REJECTED))
        }
      }
    },
    [audioChatStarted]: () => {
      const { audio: { selectedAudioInput, silenceAudio, requestByPeer } } = store.getState()
      logger('------audioChatStarted', selectedAudioInput)

      // enable and send the audio chat track to webrtc;
      // (the track to be sent is already in the store)
      if (selectedAudioInput && selectedAudioInput.track) {
        selectedAudioInput.track.enabled = true
      }

      webRtc_UpdateAudioChatTrack({ store, selectedAudioInput, silenceAudio })
      if (requestByPeer) {
        // resume audio context as a result of user interaction
        // (you clicked the accept chat button)

        // silenceAudio.context.resume()
        //   .then(() => logger(' -- audio context resumed'))
        //   .catch((err) => logger(' -- error resume audio context', err))
      }
      if (processAudioStats) {
        listenAudioStats({ store })
      }
      store.dispatch(tbCloseModal(TB_MODALS.AUDIO_CHAT_REQUEST))
    },
    [audioChatEnd]: ({ endByPeer }) => {
      // stop webrtc sending audio
      logger('------audioChatEnd')
      if (processAudioStats) {
        listenAudioStats({ enabled: false })
      }
      const { audio: { selectedAudioInput }, app: { step } } = store.getState()
      if (selectedAudioInput && selectedAudioInput.track) {
        selectedAudioInput.track.enabled = false
      }
      store.dispatch(stopListenMediaDevicesChange()) // this will also dispatch selectAudioInput(null)
      if (step === APP_STEP.CLOSING || step === APP_STEP.RTC_RESTARTING) {
        return
      }
      if (!endByPeer) {
        // you ended the chat; inform the peer
        store.dispatch(sendCommand(commandsMessageCreator.createAudioChatEnd()))
      }
    },
    [selectAudioInput]: (payload) => {
      // dispatched when (media middleware retrievs the microphone audio track):
      //   - an audio chat popup - request/answer - is displayed
      //   - the user add/remove audio devices to/from computer
      //     when an audio chat popup is displayed or during audio chat
      //   - an audio chat popup - request/answer - is closed
      //   - the audio chat is ended
      const { audio: { chatStarted, microphoneMuted, silenceAudio } } = store.getState()
      logger('------selectAudioInput', chatStarted, payload)
      // use payload.track not the selectedAudioInput.track from state
      // because middleware runs before reducers and selectedAudioInput.track may be not saved yet
      if (payload && payload.track) {
        // ensure the microphone track is enabled/disabled (the mute/unmute feature) as needed
        payload.track.enabled = (chatStarted && !microphoneMuted)
        logger('   ---track enabled', payload.track.enabled)
      }
      // rtc connection should send the microphone track to peer only when chat is started
      webRtc_UpdateAudioChatTrack({ store, selectedAudioInput: chatStarted ? payload : null, silenceAudio })
      if (chatStarted) {
        if (!payload || !payload.track) {
          // no microphone track during chat (the user pulled out the device ??)
          // send the mute command to allow the agent display the correct icon
          sendMute({ store, muted: true })
        }
        if (payload && payload.track) {
          // new microphone track during chat
          // put on it the previous mute status
          payload.track.enabled = !microphoneMuted
          sendMute({ store, muted: microphoneMuted })
        }
      }
    },
    [audioChatMuteMicrophone]: (muted) => {
      logger('------audioChatMute')
      const { audio: { chatStarted, selectedAudioInput } } = store.getState()
      if (chatStarted) {
        if (selectedAudioInput && selectedAudioInput.track) {
          selectedAudioInput.track.enabled = !muted
        }
        sendMute({ store, muted })
      }
    },
    [audioChatStatsResponse]: (statsResponse) => {
      // logger('------audioChatStatsResponse')
      const stats = processStats(statsResponse)
      store.dispatch(audioChatStats(stats))
    },
  }

  if (actions.hasOwnProperty(action.type)) {
    const invalidReason = chatActionNotValid({ action, store })
    if (invalidReason) {
      // don't execute the handler and
      // stop propagating the action (don't call next(action) anymore)
      logger(`action ${action.type} dropped, ${invalidReason}`, action)
      return Promise.resolve(store.getState())
    }
    actions[action.type](action.payload)
  }
  return next(action)
}


